import { useState, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import LoadingSpinner from '@/components/ui/loading-spinner';
import DocumentPreviewModal, {
  openDocument,
  type PreviewType,
} from '@/features/submission_forms/DocumentPreviewModal';
import { formatTimestamp } from '@/utils/dates';
import { API } from '@/utils/API';
import { readZipExport } from '@/hooks/documents/useDocumentZipExport';
import ZipExportNotice from '@/components/documents/ZipExportNotice';
import { DataTable } from '@/components/ui/DataTable';

const DOCUMENT_STATUS_MAP: Record<
  string,
  { label: string; className: string }
> = {
  draft: {
    label: 'Submitted',
    className: 'bg-blue-50 text-blue-600 border-blue-200',
  },
  final: {
    label: 'Final',
    className: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  },
};

const DEFAULT_STATUS_BADGE = {
  label: 'Unknown',
  className: 'bg-gray-50 text-gray-600 border-gray-200',
};

export function getDocumentStatusBadge(documentStatus: string) {
  return (
    DOCUMENT_STATUS_MAP[documentStatus] || {
      ...DEFAULT_STATUS_BADGE,
      label: documentStatus,
    }
  );
}

export const DOCUMENT_CATEGORIES = [
  'letters',
  'consents',
  'recruitment',
  'translations',
  'other',
] as const;

type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

const GENERATED_DOCUMENT_CATEGORIES = ['submission_form'] as const;
type GeneratedDocumentCategory = (typeof GENERATED_DOCUMENT_CATEGORIES)[number];
const FILTER_DOCUMENT_CATEGORIES = [
  ...GENERATED_DOCUMENT_CATEGORIES,
  ...DOCUMENT_CATEGORIES,
] as const;

export const CATEGORY_LABELS: Record<
  DocumentCategory | GeneratedDocumentCategory,
  string
> = {
  letters: 'Letters',
  consents: 'Consents',
  recruitment: 'Recruitment',
  translations: 'Translations',
  other: 'Other',
  submission_form: 'Submission Form',
};

function documentHasDownload(doc: { download_url: string | null }) {
  return Boolean(doc.download_url);
}

function documentHasPreview(doc: { view_url: string | null }) {
  return Boolean(doc.view_url);
}

interface DownloadResult {
  ok: boolean;
  skippedCount: number;
  /** OV-1405: set when the ZIP is being prepared in the background. */
  zipExportId?: string;
}

export interface DocumentWithStatus {
  id: string;
  filename: string;
  document_category: string | null;
  document_status: string;
  submission_id: string;
  download_url: string | null;
  view_url: string | null;
  statusLabel: string;
  statusClassName: string;
  created_at: string;
  pi_name: string | null;
}

export const DOCUMENTS_ROW_ID_PREFIX = 'doc-';

interface DocumentsTableProps {
  documents: DocumentWithStatus[];
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
  displayId?: string;
  onCategoryUpdate?: (
    documentId: string,
    submissionId: string,
    category: string
  ) => Promise<void>;
  compact?: boolean;
  collectionZipRoute?: string;
  highlightId?: string;
}

function downloadRoute(doc: DocumentWithStatus) {
  return `/submissions/${doc.submission_id}/documents/${doc.id}/download`;
}

function filenameFromContentDisposition(header: string | null): string | null {
  return header?.match(/filename="([^"]+)"/i)?.[1] ?? null;
}

async function downloadViaBlob(
  doc: DocumentWithStatus
): Promise<DownloadResult> {
  const response = await API.authenticated.fetch(downloadRoute(doc));
  if (!response.ok) return { ok: false, skippedCount: 0 };
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = doc.filename;
  link.click();
  URL.revokeObjectURL(blobUrl);
  return { ok: true, skippedCount: 0 };
}

async function downloadZipViaBlob(
  submissionId: string,
  docs: DocumentWithStatus[],
  zipRoute?: string
): Promise<DownloadResult> {
  const response = await API.authenticated.fetch(
    zipRoute ?? `/submissions/${submissionId}/documents/zip`,
    {
      method: 'POST',
      body: JSON.stringify({ document_ids: docs.map(doc => doc.id) }),
    }
  );
  // OV-1405: a large selection is prepared in the background; the table shows
  // its progress, and it downloads by itself when it is ready.
  const zipExport = await readZipExport(response);
  if (zipExport) {
    return {
      ok: true,
      skippedCount: zipExport.skipped_count,
      zipExportId: zipExport.id,
    };
  }
  if (!response.ok) return { ok: false, skippedCount: 0 };

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download =
    filenameFromContentDisposition(
      response.headers.get('content-disposition')
    ) ?? 'submission_documents.zip';
  link.click();
  URL.revokeObjectURL(blobUrl);
  return {
    ok: true,
    skippedCount:
      Number(response.headers.get('x-skipped-documents-count')) || 0,
  };
}

const ALL = 'all';

/**
 * OXX-1125. Document "Type" is hidden for now, at the customer's request, while
 * the set of document types is still being agreed (see F-11: 23 types proposed
 * on 31 August against an existing taxonomy of five, with 44% of tagged uploads
 * sitting in "other").
 *
 * Hiding it covers three surfaces that are one feature — the column, the filter
 * above the list, and the "Select type" picker inside the column, which is the
 * only place in the product where a document's type is assigned. Losing the
 * ability to categorise is intended here, not a side effect.
 *
 * TO BRING IT BACK: set this to `true`. Nothing has been deleted — the column
 * definition, the filter and the picker are all still here. There is
 * deliberately no ConfigFlag: OneVerse's flags are server-side kill-switches
 * that default on and are not read by this SPA, so wiring one would cost a new
 * key, blueprint plumbing and a separate data-migration MR for a change that is
 * otherwise one constant. Add the flag when the taxonomy is settled and the
 * requirement is "re-enable without a deploy".
 */
const DOCUMENT_TYPE_UI_ENABLED = false;

const DocumentsTable = ({
  documents,
  isLoading,
  error,
  onRetry,
  displayId,
  onCategoryUpdate,
  compact = false,
  collectionZipRoute,
  highlightId,
}: DocumentsTableProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [zipExportId, setZipExportId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<DocumentWithStatus | null>(null);
  const [previewType, setPreviewType] = useState<PreviewType | null>(null);
  const [categoryOverrides, setCategoryOverrides] = useState<
    Record<string, string>
  >({});
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);

  const visibleDocuments = useMemo(() => {
    return [...documents]
      .filter(d => d.document_category !== 'finalized_zip')
      .sort((a, b) => {
        if (a.document_category === 'submission_form') return -1;
        if (b.document_category === 'submission_form') return 1;
        return 0;
      });
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    return visibleDocuments.filter(doc => {
      const effectiveCategory =
        categoryOverrides[doc.id] ?? doc.document_category;
      if (typeFilter !== ALL && effectiveCategory !== typeFilter) return false;
      if (statusFilter !== ALL && doc.document_status !== statusFilter)
        return false;
      return true;
    });
  }, [visibleDocuments, categoryOverrides, typeFilter, statusFilter]);

  const downloadableFiltered = useMemo(
    () => filteredDocuments.filter(documentHasDownload),
    [filteredDocuments]
  );

  const allSelected =
    downloadableFiltered.length > 0 &&
    downloadableFiltered.every(d => selectedIds.has(d.id));

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        downloadableFiltered.forEach(d => next.delete(d.id));
      } else {
        downloadableFiltered.forEach(d => next.add(d.id));
      }
      return next;
    });
  }, [allSelected, downloadableFiltered]);

  const handleDownloadSelected = useCallback(async () => {
    setDownloadError(null);
    setZipExportId(null);
    const docsToDownload = downloadableFiltered.filter(d =>
      selectedIds.has(d.id)
    );
    const submissionIds = Array.from(
      new Set(docsToDownload.map(doc => doc.submission_id))
    );
    const results =
      docsToDownload.length === 1
        ? [await downloadViaBlob(docsToDownload[0])]
        : collectionZipRoute || submissionIds.length === 1
          ? [
              await downloadZipViaBlob(
                submissionIds[0],
                docsToDownload,
                collectionZipRoute
              ),
            ]
          : await Promise.all(docsToDownload.map(downloadViaBlob));

    if (results.some(result => !result.ok)) {
      setDownloadError(
        'One or more files could not be downloaded. Please try again.'
      );
      return;
    }

    // The background notice reports skipped files itself once it is ready.
    const backgroundExportId = results.find(
      result => result.zipExportId
    )?.zipExportId;
    if (backgroundExportId) {
      setZipExportId(backgroundExportId);
      return;
    }

    const skippedCount = results.reduce(
      (total, result) => total + result.skippedCount,
      0
    );
    if (skippedCount > 0) {
      setDownloadError(
        `${skippedCount} selected ${skippedCount === 1 ? 'file was' : 'files were'} not available and could not be included in the download.`
      );
    }
  }, [collectionZipRoute, downloadableFiltered, selectedIds]);

  const handleDocClick = useCallback(
    (doc: DocumentWithStatus) => {
      if (!doc.view_url) return;
      const result = openDocument(doc.filename, doc.view_url, { displayId });
      if (result) {
        setPreviewDoc(doc);
        setPreviewType(result);
      }
    },
    [displayId]
  );

  const handleCategoryChange = useCallback(
    async (doc: DocumentWithStatus, newCategory: string) => {
      setCategoryOverrides(prev => ({ ...prev, [doc.id]: newCategory }));
      try {
        await onCategoryUpdate?.(doc.id, doc.submission_id, newCategory);
      } catch {
        setCategoryOverrides(prev => {
          const next = { ...prev };
          delete next[doc.id];
          return next;
        });
      }
    },
    [onCategoryUpdate]
  );

  const columns = useMemo(() => {
    const cols: ColumnDef<DocumentWithStatus, unknown>[] = [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleSelectAll}
            disabled={downloadableFiltered.length === 0}
            className="cursor-pointer disabled:cursor-not-allowed"
          />
        ),
        enableSorting: false,
        meta: { overflow: 'nowrap', maxWidth: 40 },
        cell: ({ row }: { row: { original: DocumentWithStatus } }) => (
          <Checkbox
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={() => toggleSelect(row.original.id)}
            disabled={!documentHasDownload(row.original)}
            className="cursor-pointer disabled:cursor-not-allowed"
          />
        ),
      },
      {
        header: 'File name',
        accessorKey: 'filename',
        enableSorting: true,
        // width:'100%' makes this column greedy — it absorbs all remaining space
        // after the checkbox, status, and download columns have taken their share.
        // Works in both full-page and narrow sidebar contexts.
        meta: { overflow: 'truncate', width: '100%' },
        cell: ({ row }: { row: { original: DocumentWithStatus } }) => {
          const doc = row.original;
          return documentHasPreview(doc) ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleDocClick(doc);
              }}
              className="text-gray-700 hover:text-primary hover:underline cursor-pointer truncate text-left w-full block"
              title={doc.filename}
            >
              {doc.filename}
            </button>
          ) : (
            <span
              className="text-gray-400 truncate block w-full text-left"
              title={doc.filename}
            >
              {doc.filename}
            </span>
          );
        },
      },
    ];

    if (!compact) {
      cols.push(
        {
          header: 'Type',
          id: 'type',
          accessorFn: (row: DocumentWithStatus) =>
            categoryOverrides[row.id] ?? row.document_category,
          enableSorting: true,
          meta: { overflow: 'nowrap', maxWidth: 140 },
          cell: ({ row }: { row: { original: DocumentWithStatus } }) => {
            const doc = row.original;
            const effectiveCategory =
              categoryOverrides[doc.id] ?? doc.document_category;
            const isGeneratedDocument =
              effectiveCategory !== null &&
              GENERATED_DOCUMENT_CATEGORIES.includes(
                effectiveCategory as GeneratedDocumentCategory
              );
            const isCategorized =
              effectiveCategory &&
              (DOCUMENT_CATEGORIES.includes(
                effectiveCategory as DocumentCategory
              ) ||
                isGeneratedDocument);
            return isCategorized ? (
              <span className="text-gray-700">
                {
                  CATEGORY_LABELS[
                    effectiveCategory as
                      | DocumentCategory
                      | GeneratedDocumentCategory
                  ]
                }
              </span>
            ) : (
              <Select onValueChange={value => handleCategoryChange(doc, value)}>
                <SelectTrigger
                  className="h-8 w-32 text-xs"
                  onClick={e => e.stopPropagation()}
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          },
        },
        {
          header: 'Uploaded',
          accessorKey: 'created_at',
          enableSorting: true,
          sortingFn: 'nullableDateSort' as const,
          meta: { overflow: 'nowrap', maxWidth: 130 },
          cell: ({ getValue }: { getValue: () => string }) =>
            formatTimestamp(getValue()) ?? '—',
        },
        {
          header: 'PI',
          accessorKey: 'pi_name',
          enableSorting: true,
          meta: { overflow: 'truncate', minWidth: 120, maxWidth: 250 },
          cell: ({ getValue }: { getValue: () => string | null }) =>
            getValue() ?? '—',
        }
      );
    }

    cols.push(
      {
        header: 'Status',
        accessorKey: 'document_status',
        enableSorting: true,
        meta: { overflow: 'nowrap', maxWidth: 110 },
        cell: ({ row }: { row: { original: DocumentWithStatus } }) => (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${row.original.statusClassName}`}
          >
            {row.original.statusLabel}
          </span>
        ),
      },
      {
        id: 'download',
        header: '',
        enableSorting: false,
        meta: { overflow: 'nowrap', maxWidth: 64 },
        cell: ({ row }: { row: { original: DocumentWithStatus } }) => {
          const doc = row.original;
          return documentHasDownload(doc) ? (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                downloadViaBlob(doc);
              }}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer transition-colors inline-flex"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
          ) : (
            <span
              className="inline-flex p-1.5 text-gray-300 rounded"
              title="Download not available"
              aria-label="Download not available"
            >
              <Download className="w-4 h-4" />
            </span>
          );
        },
      }
    );

    // OXX-1125. Removed here rather than by not building it, so the definition
    // above stays intact and restoring is one constant. Keyed on the column id,
    // which is why that column carries an explicit `id: 'type'`.
    return DOCUMENT_TYPE_UI_ENABLED
      ? cols
      : cols.filter(col => col.id !== 'type');
  }, [
    compact,
    allSelected,
    downloadableFiltered,
    selectedIds,
    categoryOverrides,
    handleDocClick,
    handleCategoryChange,
    toggleSelect,
    toggleSelectAll,
  ]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className="ml-2 text-gray-600">Loading documents...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            Error loading documents
          </h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-4 text-sm text-blue-600 hover:text-blue-500"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (visibleDocuments.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No documents found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            There are no documents uploaded for this submission yet.
          </p>
        </div>
      </div>
    );
  }

  const hasSelection = filteredDocuments.some(d => selectedIds.has(d.id));

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          {!compact && DOCUMENT_TYPE_UI_ENABLED && (
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 w-36 text-sm">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {FILTER_DOCUMENT_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40 text-sm">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              <SelectItem value="draft">Submitted</SelectItem>
              <SelectItem value="final">Final</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <button
          disabled={!hasSelection}
          onClick={handleDownloadSelected}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <Download className="w-4 h-4" />
          <span>Download Selected</span>
        </button>
      </div>

      {zipExportId && (
        <ZipExportNotice
          key={zipExportId}
          exportId={zipExportId}
          className="mb-4"
        />
      )}

      {downloadError && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {downloadError}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredDocuments}
          emptyMessage="No documents match the selected filters."
          highlightId={highlightId}
          highlightIdPrefix={DOCUMENTS_ROW_ID_PREFIX}
        />
      </div>

      {previewDoc && previewType && (
        <DocumentPreviewModal
          open={!!previewDoc}
          onClose={() => {
            setPreviewDoc(null);
            setPreviewType(null);
          }}
          fileName={previewDoc.filename}
          viewUrl={previewDoc.view_url ?? ''}
          previewType={previewType}
        />
      )}
    </>
  );
};

export default DocumentsTable;
